import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  XMarkIcon, ExclamationTriangleIcon, CheckIcon, PlusIcon,
  ArrowLeftIcon, ArrowRightIcon,
} from '@heroicons/react/24/outline'
import {
  obliczMoc, porownajWarianty, wariantyMagazynu, orientacjeDla, MONTAZE, ZACIENIENIA,
  PROFILE, DODATKI, CENA_ENERGII_ZL_KWH, MAX_OSOB,
} from '../../lib/mocKalkulator'
import { zapiszZKalkulatoraMocy } from '../../lib/daneProjektu'
import { useTranslation } from 'react-i18next'
import { KRAJ, jestPolska } from '../../kraj'

/**
 * Kalkulator mocy — wyśrodkowane okno prowadzone krok po kroku, w tej samej
 * konwencji co asystent doboru: aktywny krok jest wysunięty na biały panel,
 * pozostałe zostają w tle jako podsumowanie, a po prawej stoi linia postępu.
 *
 * Liczy NA BIEŻĄCO — wynik na dole rusza się przy każdej odpowiedzi, więc widać,
 * co na co wpływa. Przycisku „Oblicz" nie ma i mieć nie będzie.
 *
 * `wymagany` = okno otwarte samo, bez prośby użytkownika (zmienia tylko podtytuł).
 * Przekazywał go przycisk „Zastanawiam się” na stronie głównej, usunięty 2026-09-30,
 * więc dziś nikt go nie ustawia. Zamknąć da się ZAWSZE: okno bez wyjścia byłoby
 * pułapką dla kogoś, kto chce tylko wrócić do formularza.
 */

// Etykiety w słowniku `glowna` (kalk.sposob.<key>). „Znam rachunek” tylko w Polsce:
// rachunek przeliczamy na kWh po polskiej cenie energii (CENA_ENERGII_ZL_KWH, w zł),
// więc kwota w innej walucie dałaby zmyślone zużycie.
const SPOSOBY = ['osoby', 'rachunek', 'kwh'].filter((key) => jestPolska || key !== 'rachunek')

const DOMYSLNE = {
  sposob: 'osoby',
  kwh: '',
  rachunek: '',
  osoby: 4,
  dodatki: [],          // [{ id, key, kwh, label }]
  montaz: 'dach',
  orientacja: 'poludnie',
  zacienienie: 'brak',
  profil: 'klasyczny',
  magazyn: false,
  // Klucz wielkości, a nie liczba kWh: po zmianie zużycia czy profilu wybór
  // „większy magazyn" ma dalej znaczyć „większy", a nie zamrożoną wartość.
  rozmiarMagazynu: 'dopasowany',
}

const zaokr = (v) => String(v).replace('.', ',')

/**
 * Etykiety i opisy z lib/mocKalkulator.js są po polsku. Tłumaczy je słownik `glowna`
 * (kalk.lib.<grupa>.<key>.<pole>), a gdy klucza brak (zawsze w polskim słowniku),
 * zostaje tekst z biblioteki. Dzięki temu polska wersja pokazuje dokładnie to, co
 * biblioteka, a zmiana tekstu w bibliotece nie rozjedzie się ze słownikiem.
 */
const tLib = (t, grupa, el, pole = 'label') => (
  el && el[pole] ? t(`kalk.lib.${grupa}.${el.key}.${pole}`, { defaultValue: el[pole] }) : el?.[pole]
)

/**
 * Ostrzeżenia `obliczMoc` przychodzą jako gotowe polskie zdania. Poza Polską
 * rozpoznajemy je po treści i podmieniamy na tłumaczenie; ostrzeżenie o granicy
 * mikroinstalacji (50 kWp) dotyczy polskiego prawa, więc tam znika. Nierozpoznane
 * zostaje po polsku: lepiej obce zdanie niż przemilczane ostrzeżenie.
 */
const RODZAJE_OSTRZEZEN = [
  ['polnoc', (o) => o.startsWith('Dach północny')],
  ['cien', (o) => o.includes('większość dnia')],
  ['trojfaza', (o) => o.startsWith('Powyżej 10 kWp')],
  ['mikro', (o) => o.includes('mikroinstalacji')],
]
const ostrzezenieKraju = (t, o) => {
  if (jestPolska) return o
  const rodzaj = RODZAJE_OSTRZEZEN.find(([, pasuje]) => pasuje(o))?.[0]
  if (rodzaj === 'mikro') return null
  return rodzaj ? t(`kalk.lib.ostrzezenie.${rodzaj}`, { defaultValue: o }) : o
}

/* ══════════════════════ elementy wspólne ══════════════════════ */

const Opcja = ({ wybrana, onClick, label, opis, ostrzega }) => {
  const { t } = useTranslation('glowna')
  return (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={wybrana}
    className={`w-full text-left px-3.5 py-2.5 rounded-xl border text-sm transition-colors ${
      wybrana
        ? 'border-blue-300 bg-blue-50 text-blue-800'
        : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-gray-50'
    }`}
  >
    <span className="font-medium">{label}</span>
    {opis && <span className={`block text-xs mt-0.5 ${wybrana ? 'text-blue-600/80' : 'text-gray-400'}`}>{opis}</span>}
    {ostrzega && wybrana && (
      <span className="block text-xs mt-1 text-amber-700">{t('kalk.ostrzega_polac')}</span>
    )}
  </button>
  )
}

const Chip = ({ wybrany, onClick, children, ikona }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={wybrany}
    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
      wybrany ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
    }`}
  >
    {ikona}
    {children}
  </button>
)

/**
 * Karta jednego wariantu instalacji. Dwie obok siebie odpowiadają na pytanie, którego
 * sama moc nie rozstrzyga: co realnie daje magazyn. Podświetlona jest ta, którą
 * wybrano w kroku 5 — i to jej moc trafia do formularza.
 */
const KartaWariantu = ({ tytul, wybrana, onWybierz, moduly, mocKwp, mocModuluW, dodatek, samowystarczalnosc }) => {
  const { t } = useTranslation('glowna')
  return (
  <button
    type="button"
    onClick={onWybierz}
    aria-pressed={wybrana}
    className={`text-left rounded-lg border px-3 py-2 transition-colors ${
      wybrana
        ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-300'
        : 'border-gray-200 bg-white hover:border-blue-200'
    }`}
  >
    <div className="flex items-baseline justify-between gap-2">
      <span className={`text-[10px] font-semibold uppercase tracking-wide ${
        wybrana ? 'text-blue-700' : 'text-gray-400'
      }`}>
        {tytul}
      </span>
      <span className="text-[11px] text-gray-500 whitespace-nowrap">
        {t('kalk.pokryjesz')}{' '}
        <span className="font-bold text-gray-900">{samowystarczalnosc}%</span>
      </span>
    </div>
    <p className="text-lg font-bold text-gray-900 leading-tight">
      {String(mocKwp).replace('.', ',')} <span className="text-xs font-semibold">kWp</span>
    </p>
    <p className="text-[11px] leading-4 text-gray-400">
      {moduly} × {mocModuluW} W
      <span className={dodatek.wyroznij ? 'text-blue-600 font-semibold' : ''}> · {dodatek.tekst}</span>
    </p>
  </button>
  )
}

/**
 * Linia postępu — ta sama logika co ProgressRail w asystencie doboru (kropka,
 * łącznik, wybrana wartość pod etykietą), ale osadzona w oknie zamiast przyklejona
 * do krawędzi ekranu. Chowa się poniżej `lg`, bo w wąskim oknie zjadłaby pytania.
 */
const LiniaKrokow = ({ kroki, aktywny, onSkocz }) => {
  const { t } = useTranslation('glowna')
  return (
  <nav aria-label={t('kalk.linia_aria')}
       className="hidden lg:flex flex-col w-48 flex-shrink-0 pl-6 border-l border-gray-100 self-start sticky top-0">
    {kroki.map((k, i) => {
      const zrobiony = i < aktywny
      const biezacy = i === aktywny
      return (
        <React.Fragment key={k.id}>
          <button
            type="button"
            onClick={() => onSkocz(i)}
            aria-current={biezacy ? 'step' : undefined}
            className="group flex items-center gap-3 text-left focus:outline-none focus-visible:ring-2
                       focus-visible:ring-blue-500 rounded-lg"
          >
            <span className="relative flex items-center justify-center h-8 w-8 flex-shrink-0">
              <span className={`flex items-center justify-center rounded-full transition-all duration-300 ${
                zrobiony ? 'h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-md shadow-blue-300/50'
                  : biezacy ? 'h-8 w-8 bg-white border-[3px] border-blue-600 shadow-[0_0_0_4px_rgba(59,130,246,0.15)]'
                    : 'h-3.5 w-3.5 bg-gray-300 group-hover:bg-gray-400'
              }`}>
                {zrobiony && <CheckIcon className="w-4 h-4" />}
                {biezacy && <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />}
              </span>
            </span>
            <span className="min-w-0">
              <span className={`block text-sm leading-tight transition-colors group-hover:text-blue-600 ${
                zrobiony ? 'text-blue-600 font-semibold' : biezacy ? 'text-gray-900 font-bold' : 'text-gray-400 font-medium'
              }`}>
                {k.label}
              </span>
              {k.wartosc && (
                <span className={`block text-xs leading-tight mt-0.5 truncate ${zrobiony ? 'text-blue-500' : 'text-gray-400'}`}>
                  {k.wartosc}
                </span>
              )}
            </span>
          </button>
          {i < kroki.length - 1 && (
            <span className={`w-[3px] h-7 rounded-full ml-[14.5px] my-1 transition-colors duration-500 ${
              zrobiony ? 'bg-gradient-to-b from-blue-500 to-blue-300' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      )
    })}
  </nav>
  )
}

/**
 * Krok: aktywny wysunięty na biel z cieniem, nieaktywne cofnięte w tło i zwinięte
 * do jednej linijki z wybraną odpowiedzią. Widać całą drogę, ale uwagę trzyma
 * jedno pytanie naraz.
 */
const Krok = ({
  numer, tytul, podtytul, aktywny, wartosc, onOtworz, children,
  onDalej, dalejZablokowany = false, etykietaDalej,
}) => {
  const { t } = useTranslation('glowna')
  return (
  <section
    className={`rounded-2xl transition-all duration-200 ${
      aktywny
        ? 'bg-white border border-blue-100 shadow-lg shadow-blue-100/50 p-5'
        : 'bg-gray-50/70 border border-gray-100 px-5 py-3'
    }`}
  >
    <button
      type="button"
      onClick={onOtworz}
      disabled={aktywny}
      className={`w-full flex items-baseline gap-2.5 text-left ${aktywny ? 'cursor-default' : 'cursor-pointer group'}`}
    >
      <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${
        aktywny ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
      }`}>
        {numer}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block font-semibold ${aktywny ? 'text-sm text-gray-900' : 'text-[13px] text-gray-500 group-hover:text-blue-600'}`}>
          {tytul}
        </span>
        {aktywny && podtytul && <span className="block text-xs text-gray-400 mt-0.5 leading-5">{podtytul}</span>}
      </span>
      {!aktywny && wartosc && (
        <span className="text-xs text-gray-500 font-medium truncate max-w-[45%] text-right">{wartosc}</span>
      )}
    </button>
    {aktywny && (
      <div className="mt-4">
        {children}
        {/* Przycisk w kroku, obok tego na dole okna: przy rozwiniętym pytaniu stopka
            bywa daleko, a szukanie „Dalej" poza obszarem, w którym się właśnie
            klikało, zrywa rytm wypełniania. */}
        {onDalej && (
          <button
            type="button"
            onClick={onDalej}
            disabled={dalejZablokowany}
            className="mt-5 w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl
                       bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500
                       active:bg-blue-700 transition-colors
                       disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {etykietaDalej ?? t('kalk.dalej')}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    )}
  </section>
  )
}

/* ══════════════════════ okno ══════════════════════ */

const KalkulatorMocy = ({ otwarty, onZamknij, onUzyj, wymagany = false, magazynWZestawie = false }) => {
  const { t } = useTranslation('glowna')
  const [f, setF] = useState(DOMYSLNE)
  const [krok, setKrok] = useState(0)
  const panelRef = useRef(null)

  useEffect(() => {
    if (!otwarty) return
    setKrok(0)
    // Wariant wybrany na stronie głównej ustawia magazyn — krok 4 nie ma
    // powtarzać decyzji, którą użytkownik podjął o chwilę wcześniej.
    setF((s) => ({ ...s, magazyn: magazynWZestawie }))
  }, [otwarty, magazynWZestawie])

  useEffect(() => {
    if (!otwarty) return undefined
    const naKlawisz = (e) => { if (e.key === 'Escape') onZamknij() }
    document.addEventListener('keydown', naKlawisz)
    const poprzedni = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', naKlawisz)
      document.body.style.overflow = poprzedni
    }
  }, [otwarty, onZamknij])

  const wynik = useMemo(() => obliczMoc(f), [f])

  /**
   * Odpowiedzi z kalkulatora zasilają później VoltValue Projekt (prognoza produkcji
   * i opłacalność). Zapisujemy je przy „użyj wyniku", a nie przy każdej zmianie
   * pola — dopiero to kliknięcie znaczy, że użytkownik uznał odpowiedzi za swoje.
   */
  const uzyjWyniku = () => {
    zapiszZKalkulatoraMocy(f, wynik.zuzycie)
    onUzyj({ ...wynik, pojemnoscMagazynuKwh: f.magazyn ? pojemnoscWybrana : null })
  }
  const magazyny = useMemo(() => wariantyMagazynu(f), [f])
  const pojemnoscWybrana = (magazyny.find((m) => m.key === f.rozmiarMagazynu) || magazyny[1]).pojemnoscKwh
  const porownanie = useMemo(
    () => porownajWarianty({ ...f, pojemnoscKwh: pojemnoscWybrana }),
    [f, pojemnoscWybrana]
  )
  const wybranyWariant = f.magazyn ? porownanie.zMagazynem : porownanie.bezMagazynu
  const orientacje = useMemo(() => orientacjeDla(f.montaz), [f.montaz])

  const ustaw = (klucz) => (wartosc) => setF((s) => ({ ...s, [klucz]: wartosc }))

  // Zmiana montażu może unieważnić wybrany kierunek (na gruncie nie ma dachu
  // płaskiego ani północy). Poprawiamy go OD RAZU, żeby w podsumowaniu kroku nie
  // świeciła odpowiedź, której formuła już nie używa.
  const ustawMontaz = (montaz) => setF((s) => {
    const mozliwe = orientacjeDla(montaz).map((o) => o.key)
    return { ...s, montaz, orientacja: mozliwe.includes(s.orientacja) ? s.orientacja : 'poludnie' }
  })

  /**
   * Chip działa jak przełącznik: ponowne kliknięcie zdejmuje pozycję z listy.
   * Wyjątkiem jest „Inne" — tam każde kliknięcie dokłada kolejny wiersz (basen,
   * warsztat, serwerownia), bo jedna pozycja „inne" nie wystarcza.
   */
  const przelaczDodatek = (key) => setF((s) => {
    const wzor = DODATKI.find((d) => d.key === key)
    if (key !== 'inne' && s.dodatki.some((d) => d.key === key)) {
      return { ...s, dodatki: s.dodatki.filter((d) => d.key !== key) }
    }
    return {
      ...s,
      dodatki: [...s.dodatki, {
        id: `${key}-${s.dodatki.length}-${s.dodatki.reduce((n, d) => n + d.key.length, 0)}`,
        key,
        kwh: wzor.kwh,
        label: wzor.wlasne ? '' : wzor.label,
      }],
    }
  })
  const zmienDodatek = (id, pole, wartosc) => setF((s) => ({
    ...s, dodatki: s.dodatki.map((d) => (d.id === id ? { ...d, [pole]: wartosc } : d)),
  }))
  const usunDodatek = (id) => setF((s) => ({ ...s, dodatki: s.dodatki.filter((d) => d.id !== id) }))

  const maZuzycie = f.sposob === 'osoby'
    || (f.sposob === 'kwh' && parseFloat(f.kwh) > 0)
    || (f.sposob === 'rachunek' && parseFloat(f.rachunek) > 0)

  const opisZuzycia = () => {
    if (f.sposob === 'kwh') return f.kwh ? `${Number(f.kwh).toLocaleString(KRAJ.locale)} kWh` : null
    if (f.sposob === 'rachunek') return f.rachunek ? t('kalk.rachunek_wartosc', { kwota: f.rachunek, waluta: KRAJ.symbol }) : null
    return `${f.osoby === MAX_OSOB ? `${MAX_OSOB}+` : f.osoby} ${f.osoby === 1 ? t('kalk.osoba') : t('kalk.osoby')}`
  }

  const opisOrientacji = tLib(t, 'orientacja', orientacje.find((o) => o.key === f.orientacja))
  const opisZacienienia = tLib(t, 'zacienienie', ZACIENIENIA.find((z) => z.key === f.zacienienie))
  const opisProfilu = tLib(t, 'profil', PROFILE.find((p) => p.key === f.profil))
  const opisMagazynu = f.magazyn ? t('kalk.tak') : t('kalk.nie')

  // Nazwa odbiornika w etykietach dla czytników: własna nazwa przy „Inne”,
  // przetłumaczona z biblioteki przy pozostałych.
  const nazwaDodatku = (d, wzor) => (
    wzor?.wlasne ? (d.label || t('kalk.k1.inne')) : (tLib(t, 'dodatek', d) || t('kalk.k1.inne'))
  )

  const KROKI = [
    { id: 'zuzycie', label: t('kalk.kroki.zuzycie'), wartosc: opisZuzycia() },
    { id: 'kierunek', label: t('kalk.kroki.kierunek'), wartosc: opisOrientacji },
    { id: 'cien', label: t('kalk.kroki.cien'), wartosc: opisZacienienia },
    { id: 'profil', label: t('kalk.kroki.profil'), wartosc: opisProfilu },
    { id: 'magazyn', label: t('kalk.kroki.magazyn'), wartosc: opisMagazynu },
  ]

  if (!otwarty) return null

  const ostatni = krok === KROKI.length - 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center sm:p-6">
      <div className="absolute inset-0 bg-gray-900/50" onClick={onZamknij} aria-hidden="true" />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t('kalk.dialog_aria')}
        className="relative w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[88vh] bg-white
                   sm:rounded-2xl shadow-2xl flex flex-col focus:outline-none"
      >
        {/* ── Nagłówek ── */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">{t('kalk.tytul')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {wymagany ? t('kalk.podtytul_wymagany') : t('kalk.podtytul')}
            </p>
          </div>
          <button type="button" onClick={onZamknij} aria-label={t('kalk.zamknij')}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex-shrink-0">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Kroki + linia postępu ── */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5">
          <div className="flex gap-6">
            <div className="flex-1 min-w-0 space-y-3">

              {/* 1. Zużycie */}
              <Krok numer="1" tytul={t('kalk.k1.tytul')} aktywny={krok === 0}
                    wartosc={opisZuzycia()} onOtworz={() => setKrok(0)}
                    onDalej={() => setKrok(1)} dalejZablokowany={!maZuzycie}>
                <div className="flex gap-1.5 mb-3">
                  {SPOSOBY.map((key) => (
                    <Chip key={key} wybrany={f.sposob === key} onClick={() => ustaw('sposob')(key)}>
                      {t(`kalk.sposob.${key}`)}
                    </Chip>
                  ))}
                </div>

                {f.sposob === 'osoby' && (
                  <>
                    <p className="text-xs text-gray-500 mb-2">{t('kalk.k1.ile_osob')}</p>
                    <div className="flex gap-1.5">
                      {Array.from({ length: MAX_OSOB }, (_, i) => i + 1).map((n) => (
                        <Chip key={n} wybrany={f.osoby === n} onClick={() => ustaw('osoby')(n)}>
                          {n === MAX_OSOB ? `${n}+` : n}
                        </Chip>
                      ))}
                    </div>
                  </>
                )}

                {f.sposob === 'rachunek' && (
                  <>
                    <div className="relative">
                      <input type="number" min="0" step="10" value={f.rachunek} autoFocus
                             onChange={(e) => ustaw('rachunek')(e.target.value)} placeholder={t('kalk.k1.rachunek_placeholder')}
                             className="w-full pl-3 pr-28 py-2.5 rounded-lg border border-gray-200 text-sm
                                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{t('kalk.k1.miesiecznie', { waluta: KRAJ.symbol })}</span>
                    </div>
                    {/* Założenie pokazane WPROST, inaczej wynik jest nie do sprawdzenia. */}
                    <p className="text-xs text-gray-400 mt-1.5">
                      {t('kalk.k1.przeliczamy', {
                        cena: CENA_ENERGII_ZL_KWH.toFixed(2).replace('.', ','), waluta: KRAJ.symbol,
                      })}
                    </p>
                  </>
                )}

                {f.sposob === 'kwh' && (
                  <div className="relative">
                    <input type="number" min="0" step="100" value={f.kwh} autoFocus
                           onChange={(e) => ustaw('kwh')(e.target.value)} placeholder={t('kalk.k1.kwh_placeholder')}
                           className="w-full pl-3 pr-24 py-2.5 rounded-lg border border-gray-200 text-sm
                                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{t('kalk.k1.kwh_rok')}</span>
                  </div>
                )}

                {/* ── Dodatkowe odbiorniki, każdy z możliwością korekty ── */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-600 mb-1">{t('kalk.k1.dodatki_tytul')}</p>
                  <p className="text-xs text-gray-400 mb-2.5">
                    {t('kalk.k1.dodatki_opis')}
                  </p>

                  <div className="flex flex-wrap gap-1.5">
                    {DODATKI.map((d) => {
                      const wybrany = f.dodatki.some((x) => x.key === d.key)
                      return (
                        <Chip key={d.key} wybrany={wybrany} onClick={() => przelaczDodatek(d.key)}
                              ikona={wybrany && !d.wlasne
                                ? <CheckIcon className="w-3 h-3" />
                                : <PlusIcon className="w-3 h-3" />}>
                          {tLib(t, 'dodatek', d)}
                          {!d.wlasne && (
                            <span className={wybrany ? 'text-blue-200 ml-0.5' : 'text-gray-400 ml-0.5'}>
                              {d.kwh.toLocaleString(KRAJ.locale)} kWh
                            </span>
                          )}
                        </Chip>
                      )
                    })}
                  </div>

                  {f.dodatki.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {f.dodatki.map((d) => {
                        const wzor = DODATKI.find((x) => x.key === d.key)
                        return (
                          <div key={d.id} className="flex items-center gap-2">
                            {wzor?.wlasne ? (
                              <input type="text" value={d.label} maxLength={40} autoFocus
                                     onChange={(e) => zmienDodatek(d.id, 'label', e.target.value)}
                                     placeholder={t('kalk.k1.nazwa_placeholder')}
                                     aria-label={t('kalk.k1.nazwa_aria')}
                                     className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 text-sm
                                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            ) : (
                              <span className="flex-1 min-w-0 text-sm text-gray-700 truncate">{tLib(t, 'dodatek', d)}</span>
                            )}
                            <div className="relative w-28 flex-shrink-0">
                              <input type="number" min="0" step="100" value={d.kwh}
                                     onChange={(e) => zmienDodatek(d.id, 'kwh', e.target.value)}
                                     aria-label={t('kalk.k1.zuzycie_aria', { nazwa: nazwaDodatku(d, wzor) })}
                                     className="w-full pl-2.5 pr-11 py-2 rounded-lg border border-gray-200 text-sm
                                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">kWh</span>
                            </div>
                            <button type="button" onClick={() => usunDodatek(d.id)}
                                    aria-label={t('kalk.k1.usun_aria', { nazwa: nazwaDodatku(d, wzor) })}
                                    className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex-shrink-0">
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </Krok>

              {/* 2. Kierunek instalacji */}
              <Krok numer="2" tytul={t('kalk.k2.tytul')}
                    podtytul={t('kalk.k2.podtytul')} aktywny={krok === 1}
                    wartosc={opisOrientacji}
                    onOtworz={() => setKrok(1)} onDalej={() => setKrok(2)}>
                <div className="flex gap-1.5 mb-4">
                  {MONTAZE.map((m) => (
                    <Chip key={m.key} wybrany={f.montaz === m.key} onClick={() => ustawMontaz(m.key)}>
                      {tLib(t, 'montaz', m)}
                    </Chip>
                  ))}
                </div>
                {f.montaz === 'grunt' && (
                  <p className="text-xs text-gray-400 mb-2.5">
                    {t('kalk.k2.grunt')}
                  </p>
                )}
                <div className="space-y-1.5">
                  {orientacje.map((o) => (
                    <Opcja key={o.key} wybrana={f.orientacja === o.key} onClick={() => ustaw('orientacja')(o.key)}
                           label={tLib(t, 'orientacja', o)} opis={tLib(t, 'orientacja', o, 'opis')}
                           ostrzega={o.odradzane} />
                  ))}
                </div>
              </Krok>

              {/* 3. Zacienienie */}
              <Krok numer="3" tytul={t('kalk.k3.tytul')}
                    podtytul={t('kalk.k3.podtytul')}
                    aktywny={krok === 2}
                    wartosc={opisZacienienia}
                    onOtworz={() => setKrok(2)} onDalej={() => setKrok(3)}>
                <div className="space-y-1.5">
                  {ZACIENIENIA.map((z) => (
                    <Opcja key={z.key} wybrana={f.zacienienie === z.key} onClick={() => ustaw('zacienienie')(z.key)}
                           label={tLib(t, 'zacienienie', z)} opis={tLib(t, 'zacienienie', z, 'opis')} />
                  ))}
                </div>
              </Krok>

              {/* 4. Profil zużycia */}
              <Krok numer="4" tytul={t('kalk.k4.tytul')}
                    podtytul={t('kalk.k4.podtytul')}
                    aktywny={krok === 3}
                    wartosc={opisProfilu}
                    onOtworz={() => setKrok(3)}
                    onDalej={() => setKrok(4)}>
                <div className="space-y-1.5">
                  {PROFILE.map((p) => (
                    <Opcja key={p.key} wybrana={f.profil === p.key} onClick={() => ustaw('profil')(p.key)}
                           label={tLib(t, 'profil', p)} opis={tLib(t, 'profil', p, 'opis')} />
                  ))}
                </div>
              </Krok>

              {/* 5. Magazyn energii */}
              <Krok numer="5" tytul={t('kalk.k5.tytul')}
                    aktywny={krok === 4}
                    wartosc={opisMagazynu}
                    onOtworz={() => setKrok(4)}
                    onDalej={uzyjWyniku}
                    dalejZablokowany={!maZuzycie}
                    etykietaDalej={t('kalk.k5.uzyj')}>
                <p className="text-[13px] text-gray-600 leading-6 mb-3">
                  {t('kalk.k5.opis_1')}
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  {t('kalk.k5.opis_2')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Opcja wybrana={f.magazyn} onClick={() => ustaw('magazyn')(true)}
                         label={t('kalk.tak')} opis={t('kalk.k5.tak_opis')} />
                  <Opcja wybrana={!f.magazyn} onClick={() => ustaw('magazyn')(false)}
                         label={t('kalk.nie')} opis={t('kalk.k5.nie_opis')} />
                </div>

                {f.magazyn && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-600 mb-1">{t('kalk.k5.pojemnosc')}</p>
                    {/* Trzy wielkości obok siebie zamiast jednej liczby: dopiero
                        zestawienie pokazuje, gdzie dokładanie kilowatogodzin przestaje
                        się opłacać. Bateria większa niż nocne zapotrzebowanie stoi
                        pusta niezależnie od tego, ile ma pojemności. */}
                    <p className="text-xs text-gray-400 mb-2.5">
                      {t('kalk.k5.pojemnosc_opis')}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {magazyny.map((m) => {
                        const wybrany = f.rozmiarMagazynu === m.key
                        return (
                          <button
                            key={m.key}
                            type="button"
                            onClick={() => ustaw('rozmiarMagazynu')(m.key)}
                            aria-pressed={wybrany}
                            className={`rounded-xl border px-2 py-2.5 text-center transition-colors ${
                              wybrany
                                ? 'border-blue-300 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50'
                            }`}
                          >
                            <span className={`block text-[10px] font-semibold uppercase tracking-wide ${
                              wybrany ? 'text-blue-700' : 'text-gray-400'
                            }`}>
                              {tLib(t, 'magazyn', m)}
                            </span>
                            <span className="block text-base font-bold text-gray-900 leading-tight mt-0.5">
                              {zaokr(m.pojemnoscKwh)} <span className="text-[11px] font-semibold">kWh</span>
                            </span>
                            <span className="block text-[11px] text-gray-500 mt-0.5">
                              {t('kalk.pokryjesz')}{' '}
                              <span className="font-bold text-gray-900">{m.samowystarczalnoscProc}%</span>
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </Krok>

              <p className="text-xs text-gray-400 leading-5 pt-1">
                {t('kalk.zastrzezenie')}
              </p>
            </div>

            <LiniaKrokow kroki={KROKI} aktywny={krok} onSkocz={setKrok} />
          </div>
        </div>

        {/* ── Wynik + nawigacja ── */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-5 sm:px-6 py-4">
          {wynik.ostrzezenia
            .map((o) => ({ o, tekst: ostrzezenieKraju(t, o) }))
            .filter(({ tekst }) => tekst)
            .map(({ o, tekst }) => (
            <p key={o} className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border
                                  border-amber-200 rounded-lg px-2.5 py-2 mb-2 leading-5">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {tekst}
            </p>
          ))}

          <div className="grid grid-cols-2 gap-2.5 mb-2.5">
            <KartaWariantu
              tytul={t('kalk.wariant.z_magazynem')}
              wybrana={f.magazyn}
              onWybierz={() => ustaw('magazyn')(true)}
              moduly={porownanie.zMagazynem.liczbaModulow}
              mocKwp={porownanie.zMagazynem.mocKwp}
              mocModuluW={wynik.mocModuluW}
              dodatek={{ tekst: t('kalk.wariant.plus_magazyn', { kwh: zaokr(porownanie.zMagazynem.pojemnoscKwh) }), wyroznij: true }}
              samowystarczalnosc={porownanie.zMagazynem.samowystarczalnoscProc}
            />
            <KartaWariantu
              tytul={t('kalk.wariant.bez_magazynu')}
              wybrana={!f.magazyn}
              onWybierz={() => ustaw('magazyn')(false)}
              moduly={porownanie.bezMagazynu.liczbaModulow}
              mocKwp={porownanie.bezMagazynu.mocKwp}
              mocModuluW={wynik.mocModuluW}
              dodatek={{ tekst: t('kalk.wariant.sama_pv'), wyroznij: false }}
              samowystarczalnosc={porownanie.bezMagazynu.samowystarczalnoscProc}
            />
          </div>

          {/* Szczegóły pomocnicze, chowane na telefonie. Moc i liczba modułów są już
              w karcie, a stopka razem z porównaniem zajmowała tam pół ekranu. */}
          <p className="hidden sm:block text-[11px] text-gray-400 leading-4 mb-3">
            {t('kalk.podsumowanie', {
              count: wybranyWariant.liczbaModulow,
              m2: wybranyWariant.powierzchniaM2,
              kwh: wybranyWariant.produkcjaKwh.toLocaleString(KRAJ.locale),
              kwp: zaokr(wybranyWariant.mocKwp),
            })}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setKrok((k) => Math.max(0, k - 1))}
              disabled={krok === 0}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white
                         text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              {t('kalk.wstecz')}
            </button>

            {ostatni ? (
              <button
                type="button"
                onClick={uzyjWyniku}
                disabled={!maZuzycie}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600
                           text-white font-semibold hover:bg-blue-500 active:bg-blue-700 transition-colors
                           disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <CheckIcon className="w-5 h-5" />
                {t('kalk.k5.uzyj')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setKrok((k) => Math.min(KROKI.length - 1, k + 1))}
                disabled={!maZuzycie}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600
                           text-white font-semibold hover:bg-blue-500 active:bg-blue-700 transition-colors
                           disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {t('kalk.dalej')}
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          {!maZuzycie && (
            <p className="mt-2 text-center text-xs text-amber-600">
              {f.sposob === 'kwh' ? t('kalk.podaj_kwh') : t('kalk.podaj_rachunek')}
            </p>
          )}

          {/* Na wąskim ekranie linii postępu nie ma, zostaje sam licznik kroków. */}
          <p className="lg:hidden mt-2 text-center text-xs text-gray-400">
            {t('kalk.krok_z', { n: krok + 1, z: KROKI.length })}
          </p>
        </div>
      </div>
    </div>
  )
}

export default KalkulatorMocy
